'use client';

import { useEffect, useState } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Whether the visitor has asked for reduced motion: null until known, then
 * kept current.
 *
 * Starts null so the server render and the first client render agree and so
 * callers can wait rather than assume; then reads the real preference and
 * subscribes to changes, so flipping the OS setting mid-visit takes effect
 * without a reload. Browsers without matchMedia resolve to false.
 */
export function useReducedMotion(): boolean | null {
  const [reduced, setReduced] = useState<boolean | null>(null);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      const id = setTimeout(() => setReduced(false), 0);
      return () => clearTimeout(id);
    }
    const query = window.matchMedia(QUERY);
    // Deferred to the next tick so the effect itself does not set state
    // synchronously and cascade a second render before this one commits.
    const initial = setTimeout(() => setReduced(query.matches), 0);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => {
      clearTimeout(initial);
      query.removeEventListener('change', onChange);
    };
  }, []);

  return reduced;
}
